/** CONF-258: a saved quote's `execMetrics.overrides` may predate the per-node
 *  model — the old shape kept 6 capacity and 6 system-total-throughput keys
 *  (see gen2-dev's MetricsOverrides.ts prior to this change). This module:
 *  - splits a saved payload into overrides the current model understands,
 *    old-shaped entries that still need conversion once the live
 *    `ThroughputBasis` is known, and anything neither recognizes;
 *  - converts a legacy throughput override into per-node terms against that
 *    basis (capacity has no per-node equivalent and is always dropped); and
 *  - renders the one-time notice shown after a quote is hydrated and
 *    converted.
 *  Pure and basis-agnostic: callers decide *when* the basis is trustworthy
 *  (see docs/superpowers/specs/2026-10-31-conf-258-custom-metrics-per-node-design.md
 *  §6's timing note) and run the conversion then. */

import type { PerNodeKey, ThroughputBasis } from '../metrics/types';
import { METRIC_OVERRIDE_KEYS, type MetricOverride, type MetricOverrides } from './MetricsOverrides';

/** Old display-metric key (what a legacy override targeted) -> its new per-node key. */
export const LEGACY_THROUGHPUT_KEYS: Readonly<Record<string, PerNodeKey>> = Object.fromEntries(
  METRIC_OVERRIDE_KEYS.map((m) => [m.metric, m.key]),
);

/** The six capacity keys retired by CONF-258, verbatim from gen2-dev's MetricsOverrides.ts. */
export const LEGACY_CAPACITY_KEYS: readonly string[] = [
  'totalAfaRawCapacity', 'afaEffectiveCapacity', 'afaCompressedEffectiveCapacity',
  'totalHybridRawCapacity', 'hybridEffectiveCapacity', 'hybridCompressedEffectiveCapacity',
];

export type LegacyOverrides = Record<string, MetricOverride>;

const CURRENT_KEYS = new Set<string>(METRIC_OVERRIDE_KEYS.map((m) => m.key));

function hasFiniteNumber(v: unknown, field: string): boolean {
  if (!v || typeof v !== 'object') return false;
  const n = (v as Record<string, unknown>)[field];
  return typeof n === 'number' && Number.isFinite(n);
}

/** A new-key entry needs both `value` and `baseline` to be finite numbers. */
const isFiniteOverride = (v: unknown): v is MetricOverride => hasFiniteNumber(v, 'value') && hasFiniteNumber(v, 'baseline');

/** A legacy entry only needs a finite `value` — conversion never reads the
 *  old `baseline` (it is replaced by the live basis rating), so a stale or
 *  even malformed old baseline doesn't disqualify the entry. */
const hasFiniteValue = (v: unknown): v is { value: number; baseline: unknown } => hasFiniteNumber(v, 'value');

/** `saved`: a new key with a finite value/baseline goes to `current`; an old
 *  throughput or capacity key with a finite value goes to `legacy`; anything
 *  else (unknown key, or a known key with a non-finite number) goes to `invalid`. */
export function partitionSavedOverrides(saved: unknown): { current: MetricOverrides; legacy: LegacyOverrides; invalid: string[] } {
  const current: MetricOverrides = {};
  const legacy: LegacyOverrides = {};
  const invalid: string[] = [];
  if (!saved || typeof saved !== 'object') return { current, legacy, invalid };

  for (const [key, value] of Object.entries(saved as Record<string, unknown>)) {
    const isLegacyKey = key in LEGACY_THROUGHPUT_KEYS || LEGACY_CAPACITY_KEYS.includes(key);
    if (CURRENT_KEYS.has(key) && isFiniteOverride(value)) {
      current[key] = value;
    } else if (isLegacyKey && hasFiniteValue(value)) {
      legacy[key] = value as MetricOverride;
    } else {
      invalid.push(key);
    }
  }
  return { current, legacy, invalid };
}

/** Converts each legacy throughput entry into a per-node override against the
 *  live basis: `value = old.value / entry.multiplier`, `baseline =
 *  entry.rating` (the old baseline is discarded — see module doc). A capacity
 *  key, a key with no basis entry, or a multiplier that is ≤ 0 or non-finite
 *  is dropped instead of converted. */
export function convertLegacyMetricOverrides(
  legacy: LegacyOverrides, basis: ThroughputBasis,
): { overrides: MetricOverrides; converted: string[]; dropped: string[] } {
  const overrides: MetricOverrides = {};
  const converted: string[] = [];
  const dropped: string[] = [];

  for (const [key, old] of Object.entries(legacy)) {
    const perNodeKey = LEGACY_THROUGHPUT_KEYS[key];
    const entry = perNodeKey ? basis[perNodeKey] : undefined;
    if (!perNodeKey || !entry || !Number.isFinite(entry.multiplier) || entry.multiplier <= 0) {
      dropped.push(key);
      continue;
    }
    overrides[perNodeKey] = { value: old.value / entry.multiplier, baseline: entry.rating };
    converted.push(key);
  }
  return { overrides, converted, dropped };
}

/** The one-time notice shown after a legacy quote is converted. `null` when
 *  nothing needed converting or dropping (a quote already in the new shape).
 *  Branches on WHAT was dropped, not just whether anything was: a dropped
 *  capacity key gets the "custom capacity" wording, but a dropped throughput
 *  key that simply couldn't convert (e.g. its set is now disabled) is not
 *  capacity and must not be described as such. */
export function legacyNoticeMessage(r: { converted: string[]; dropped: string[] }): string | null {
  const converted = r.converted.length > 0;
  const dropped = r.dropped.length > 0;
  const droppedCapacity = r.dropped.some((k) => LEGACY_CAPACITY_KEYS.includes(k));
  if (converted && droppedCapacity) {
    return "This quote's custom metrics were converted to per-node values; custom capacity and any values that could not be converted were removed.";
  }
  if (converted && dropped) {
    return "This quote's custom metrics were converted to per-node values; some could not be converted and were removed.";
  }
  if (converted) return "This quote's custom metrics were converted to per-node values.";
  if (droppedCapacity) return "This quote's custom capacity and any values that could not be converted were removed.";
  if (dropped) return "This quote's custom metrics could not be converted to per-node values and were removed.";
  return null;
}
